import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { SVG } from "@components/items/common/SVG";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import TranslatedText from "@components/items/display/TranslatedText";
import FormTextInput from "@components/items/inputs/FormTextInput";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@ui/card";
import { emailRegex } from "@utils/helpers/emailValidation";
import { FieldType } from "@utils/types/components";
import { checkEmail, clearEmailExist, useCheckEmail } from "@lib/stores";

import { LoginByEmail } from "./LoginByEmail";
import { SignupByEmail } from "./SignUpByEmail";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";

export const AuthByEmail = () => {
  const [email, setEmail] = useState("");

  const { data: emailExist } = useCheckEmail();

  return (
    <div className="w-full">
      {emailExist === null ? (
        <EmailDetection
          onSubmit={(email) => {
            setEmail(email);
          }}
        />
      ) : email ? (
        emailExist ? (
          <LoginByEmail
            email={email}
            onCancel={() => {
              setEmail("");

              clearEmailExist();
            }}
          />
        ) : (
          <SignupByEmail
            email={email}
            onCancel={() => {
              setEmail("");

              clearEmailExist();
            }}
          />
        )
      ) : null}
    </div>
  );
};

export type EmailInput = {
  email: string;
};

interface EmailDetectionProps {
  onSubmit: (email: string) => void;
}

const EmailDetection = ({ onSubmit }: EmailDetectionProps) => {
  const navigate = useAppNavigate();

  const { status } = useCheckEmail();

  const {
    handleSubmit,
    control,
    formState: { isDirty, errors },
  } = useForm<EmailInput>({ mode: "all", defaultValues: { email: "" } });

  const isDisabled =
    !isDirty || Object.keys(errors).length !== 0 || status === "loading";

  const onFormSubmit: SubmitHandler<EmailInput> = (data) => {
    checkEmail(data.email, (res) => {
      onSubmit(data.email);
    });
  };

  return (
    <Card className="flex w-full flex-col items-center justify-center rounded-xl border-dark_background bg-dark_background py-3 text-white sm:w-[434px]">
      <CardHeader>
        <CardTitle>
          <TranslatedText
            text={`onboarding.titles.${"continue using email"}`}
            className="text-[20px] font-normal"
          />
        </CardTitle>
      </CardHeader>
      <form className="w-full">
        <CardContent className="my-3 flex w-full items-center justify-center px-8">
          <FormTextInput
            inputExtraClassNames="placeholder:rtl:pr-1 placeholder:ltr:pl-1 !bg-[#D9D9D9]"
            control={control}
            name={`email`}
            fieldMeta={{
              fieldType: FieldType.Text,
              placeHolder: "name@gmail.com",
              fieldOptions: { pattern: emailRegex() },
              required: true,
            }}
            required
            inputHtmlType="email"
            prefix={
              <SVG
                src="/assets/icons/email.svg"
                preProcessor={(code: string) =>
                  code
                    .replace(/(fill=)"(?!none")[^"]+"/g, 'fill="currentColor"')
                    .replace(
                      /(stroke=)"(?!none")[^"]+"/g,
                      'stroke="currentColor"'
                    )
                }
                className="text-primary"
              />
            }
            validationErrorMessages={{
              pattern: "invalid email",
            }}
            onEnter={() => {
              handleSubmit(onFormSubmit)();
            }}
          />
        </CardContent>
        <CardFooter className="flex w-full items-center justify-center gap-5">
          <ShadcnButton
            className="bg-transparent hover:bg-transparent/20"
            onClick={() => {
              navigate("/login");
            }}
            type="button"
          >
            <TranslatedText
              text={`onboarding.buttons.${"cancel"}`}
              className="text-[16px] font-normal text-white"
            />
          </ShadcnButton>
          <ShadcnButton
            disabled={isDisabled}
            onClick={handleSubmit(onFormSubmit)}
            isLoading={status === "loading"}
            className="text-white"
          >
            <TranslatedText
              text={`onboarding.buttons.${"continue"}`}
              className="text-[16px] font-normal leading-[22px]"
            />
          </ShadcnButton>
        </CardFooter>
      </form>
    </Card>
  );
};
